import React, { useState, useEffect, useMemo } from "react"; // Add useEffect
import { useSearchParams } from "react-router-dom"; // Add this import
import { PageHeader } from "../../components/PageHeader";
import { Controller, FormProvider, useForm } from "react-hook-form";
import ComboBox from "../../components/ComboBox";
import {
  useCreateProviderUser,
  useGetprovidersListForProviderDetails,
  useGetPurchaseVendorBySponsor,
  useListOfProviderLocations,
  useListOfQuestionnaireTemplate,
  useSendRfiToPurchaseVendor,
  useUpdateProviderOrg,
  useUpdateProviderEmail,
  useAddProviderContact,
  useAddProviderLocation,
  UseGetRfiListForFilters,
} from "../../custom-hooks/queries";
import { existingRFIRequestValidationSchema } from "../../Schema/RequestRfiSchema";
import { yupResolver } from "@hookform/resolvers/yup";
import Select, { components } from "react-select";
import { animatedComponents, newVendorRightsList } from "../../utils/constants";
import TemplateGroupedDropdown from "../../components/TemplateGroupedDropdown";
import { useSelector } from "react-redux";
import DatePickerController from "../../components/datePickerController";
import { useNavigate } from "react-router-dom";
import NotificationModal from "../../components/NotificationModal";
import { dateFormatRFi } from "../../utils/helperMethods";
import LoadingModal from "../../components/Skeletons/LoadingModal";
import { useUser } from "@clerk/clerk-react";
import ProviderContactDrawer from "../../components/ProviderContactDrawer";
import { v4 as uuidv4 } from "uuid";

import { CheckIcon } from "@heroicons/react/20/solid";
import { ChevronDownIcon as ChevronDownIconSolid } from "@heroicons/react/20/solid";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  countryOptions,
  getCountryByValue,
  getFilteredCountryOptions,
  getFilteredStateOptions,
  getStateOptions,
  parseAddressComponents,
} from "../../utils/locationData";
import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";
import { getGoogleMapsApiKey } from "../../config/googleMapsConfig";
import { onBlurSelectWithDelay } from "../../utils/dom-events";
import LocationFields from "../../components/LocationFields";
import ContactFields from "../../components/ContactFields";
import { getLocationFromProvider } from "../../utils/addressUtils";
import CustomTooltip from "../../components/Filters/CustomTooltip";
import { isDuringMaintenance } from "../../utils/maintenance";

const newContactObj = {
  id: "new-contact",
  firstName: "",
  lastName: "",
  email: "",
  fullContact: "",
  primary: false,
};

export const RequestRFis = () => {
  // State for contact drawer
  const [contactDrawerOpen, setContactDrawerOpen] = useState(false);
  const [additionalContacts, setAdditionalContacts] = useState([]);

  // Handler to add new contact from drawer
  const handleAddContact = (contact) => {
    setAdditionalContacts((prev) => [...prev, contact]);
  };
  const navigate = useNavigate();
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [isError, setIsError] = useState(false);

  // const { data: providers } = useGetprovidersListForProviderDetails();
  const { data: providers } = useGetprovidersListForProviderDetails();
  const { data: questionnares } = useListOfQuestionnaireTemplate();
  const { data: rfis } = UseGetRfiListForFilters();
  const { mutate: sendRfis } = useSendRfiToPurchaseVendor();
  const [isLoading, setLoading] = useState(false);
  const methods = useForm({
    resolver: yupResolver(existingRFIRequestValidationSchema),
    mode: "onTouched",
    defaultValues: { rfiLocationId: "" },
  });
  const { mutate: addProviderUser } = useCreateProviderUser();
  const { mutate: updateProviderOrg } = useUpdateProviderOrg();
  const { mutate: updateProviderEmail } = useUpdateProviderEmail();
  const { mutateAsync: addProviderContact } = useAddProviderContact();
  const { mutateAsync: addProviderLocation } = useAddProviderLocation();

  const [isRFIContactDropdownOpen, setIsRFIContactDropdownOpen] =
    useState(false);
  const [currentSelectedRFIContact, currentSetSelectedRFIContact] =
    useState(null);

  const [primaryAddress, setPrimaryAddress] = useState("");
  const [primaryCity, setPrimaryCity] = useState("");
  const [primaryCountry, setPrimaryCountry] = useState("");
  const [primaryState, setPrimaryState] = useState("");
  const [primaryCountrySearch, setPrimaryCountrySearch] = useState("");
  const [primaryStateSearch, setPrimaryStateSearch] = useState("");
  const [isRFILocationDropdownOpen, setIsRFILocationDropdownOpen] =
    useState(false);
  const [currentSelectedRFILocation, currentSetSelectedRFILocation] =
    useState(null);

  const [autocomplete, setAutocomplete] = useState(null);
  const [isPrimaryCountryDropdownOpen, setIsPrimaryCountryDropdownOpen] =
    useState(false);
  const [isPrimaryStateDropdownOpen, setIsPrimaryStateDropdownOpen] =
    useState(false);

  const {
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    control,
    register,
    formState: { errors },
    reset,
  } = methods;
  // Sync selected RFI location id to form for validation (primary location required only when "new-location")
  useEffect(() => {
    setValue("rfiLocationId", currentSelectedRFILocation?.id ?? "");
  }, [currentSelectedRFILocation, setValue]);
  const { data: providerLocations, refetch: refetchLocations } =
    useListOfProviderLocations(watch("selectedProvider")?.provider_id);
  const { data: purchaseVendorBySponsor } = useGetPurchaseVendorBySponsor(
    watch("selectedProvider")?.provider_id,
  );

  const { user } = useUser();
  const { hcUserId } = useSelector((state) => state.userRights);

  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? "#AB54DB" : "white", // Purple background on hover
      color: state.isFocused ? "white" : "black", // White text on hover for better contrast
    }),
  };

  const optionsForMultiSelectLocations = useMemo(() => {
    // Debug the structure

    // Check if data exists and is an array
    if (!providerLocations || !Array.isArray(providerLocations)) {
      return [];
    }

    // Create options array
    const options = [];

    // Process each provider in the array
    providerLocations.forEach((provider) => {
      // Check if provider has provider_locations array
      if (
        provider.provider_locations &&
        Array.isArray(provider.provider_locations)
      ) {
        // Sort locations by location_type: primary, secondary, additional
        const sortedLocations = provider.provider_locations.sort((a, b) => {
          const typeOrder = { primary: 1, secondary: 2, additional: 3 };
          const aOrder = typeOrder[a.location_type] || 4;
          const bOrder = typeOrder[b.location_type] || 4;
          return aOrder - bOrder;
        });

        // Process each location in the sorted provider_locations array
        sortedLocations.forEach((location) => {
          // Add address1 as an option if it exists
          if (location.address1) {
            // For primary locations, append provider's city and country to address1
            let addressLabel = location.address1;
            if (location.location_type === "primary") {
              const cityCountry = [];
              if (provider.city) cityCountry.push(provider.city);
              if (provider.country) cityCountry.push(provider.country);

              if (cityCountry.length > 0) {
                addressLabel = `${location.address1}, ${cityCountry.join(
                  ", ",
                )}`;
              }
            }

            options.push({
              value: `${location.provider_location_id}_address1`,
              label: addressLabel,
              locationId: location.provider_location_id,
              addressType: "address1",
              locationType: location.location_type,
            });
          }

          // Add address2 as a separate option if it exists
          if (location.address2) {
            options.push({
              value: `${location.provider_location_id}_address2`,
              label: location.address2,
              locationId: location.provider_location_id,
              addressType: "address2",
              locationType: location.location_type,
            });
          }
        });
      }
    });

    return options;
  }, [providerLocations]);

  const filtered = questionnares?.filter((item) => item?.name !== "Core RFI");

  const optionsForMultiSelect = filtered?.map((item) => ({
    value: item?.hash_id,
    label: item?.name,
    global: item?.global,
    owner_id: item?.id,
  }));

  const [searchParams] = useSearchParams();
  const providerId = searchParams.get("id");
  const templateId = searchParams.get("templateId");

  useEffect(() => {
    if (providerId && providers?.length > 0) {
      const selectedProvider = providers.find(
        (p) => p.provider_id === providerId,
      );
      if (selectedProvider) {
        setValue("selectedProvider", selectedProvider);
        // Don't auto-select a contact here
        setValue("selectedContact", null);
      }
    }

    if (templateId && optionsForMultiSelect?.length > 0) {
      const selectedQuestonnarie = optionsForMultiSelect.find(
        (p) => p.value === templateId,
      );
      if (selectedQuestonnarie) {
        setValue("serviceCategories", [selectedQuestonnarie]);
      }
    }
  }, [providerId, templateId, providers, setValue]);

  // Watch the selected provider
  const selectedProvider = watch("selectedProvider");
  // Get contacts for the selected provider
  const getContactsForProvider = (provider) => {
    if (!provider) return [];

    // Get provider users if available
    if (provider.provider_user?.length > 0) {
      return provider.provider_user;
    }

    // Otherwise use contact_email as fallback (if available)
    if (provider.contact_email) {
      const fallbackContact = {
        name: provider.contact_name || "",
        email: provider.contact_email,
      };
      return [fallbackContact];
    }

    return [];
  };

  const existingLocations = {};
  const availableLocations = useMemo(
    () => [
      ...(providerLocations
        ?.map((loc) => loc.provider_locations)
        ?.flat()
        ?.filter((loc) => loc.address || loc.address1)
        ?.sort((a, b) => a.location_type === "primary" ? -1 : a.created_at > b.created_at ? 1 : -1)
        ?.map((loc, i) => ({
          ...loc,
          id: Date.now() + i,
          address: loc.address || loc.address1,
          label:
            loc.location_type === "primary" || i === 0
              ? "Primary Location"
              : `Additional Location ${i}`,
          fullAddress: getLocationFromProvider(loc, { short: false, abbvState: true }),
        }))
        .filter((loc) => {
          const exist = !existingLocations[loc.fullAddress];
          existingLocations[loc.fullAddress] = true;
          return exist;
        }) || []),
      {
        id: "new-location",
        label: "",
        fullAddress: "",
      },
    ],
    [providerLocations],
  );

  // Replace the apiContacts useMemo with a regular function
  const getApiContacts = () => {
    const selectedProvider = watch("selectedProvider");

    // Move the conditional logic inside the hook function, not as part of the hook call itself
    if (!selectedProvider) {
      return [];
    }

    // Get provider users if available
    if (selectedProvider.provider_user?.length > 0) {
      return selectedProvider.provider_user;
    }

    // Otherwise use contact_email as fallback (if available)
    if (selectedProvider.contact_email) {
      const fallbackContact = {
        name: selectedProvider.contact_name || "",
        email: selectedProvider.contact_email,
      };
      return [fallbackContact];
    }

    return [];
  };

  // Calculate contacts directly without useMemo
  const apiContacts = getApiContacts();
  const allContacts = [...apiContacts, ...additionalContacts];
  // Vendor has "displayable" contacts only if at least one API contact has first_name (dropdown filters by loc.firstName from contact.first_name)
  const hasDisplayableApiContacts = apiContacts.some(
    (c) => String(c.first_name || "").trim() !== "",
  );
  useEffect(() => {
    if (selectedProvider) {
      // When vendor has no contacts (or none with name/first_name so dropdown only shows "Add a contact..."), show additional contact section immediately
      if (!hasDisplayableApiContacts) {
        currentSetSelectedRFIContact(newContactObj);
        setAdditionalContacts([newContactObj]);
      } else {
        // Vendor has displayable contacts: auto-select the first and clear additional
        if (allContacts[0]?.email !== currentSelectedRFIContact?.email) {
          currentSetSelectedRFIContact(allContacts[0]);
          setAdditionalContacts([]);
        }
      }
    }
  }, [selectedProvider, hasDisplayableApiContacts, setValue]);

  const availableContacts = [
    ...(allContacts
      .map((contact, index) => ({
        id: contact.email || `contact-${index}`, // Add unique id for each contact
        ...contact, // Keep all original properties
        firstName: contact.first_name || "",
        lastName: contact.last_name || "",
        name: contact.name || "",
        email: contact.email || "",
        fullContact: [
          [contact.first_name, contact.last_name, contact.title].join(" "),
          // contact.email,
        ].join(", "),
        label:
          index === 0 && contact.email === apiContacts[0]?.email
            ? "Preferred Contact"
            : `Additional Contact ${index}`,
        // Set primary flag for the first provider_user contact
        primary: index === 0 && contact.email === apiContacts[0]?.email,
      }))
      .filter((loc) => loc.firstName) || []),
    newContactObj,
  ];

  useEffect(() => {
    if (selectedProvider && availableLocations.length > 0) {
      currentSetSelectedRFILocation(availableLocations?.find(loc => loc.location_type === "primary") || availableLocations[0]);
    }
  }, [selectedProvider, availableLocations]);

  useEffect(() => {
    if (
      availableContacts.length > 0 &&
      !currentSelectedRFIContact &&
      availableContacts[0].id !== "new-contact"
    ) {
      currentSetSelectedRFIContact(availableContacts[0]);
    }
    if (
      currentSelectedRFIContact &&
      !availableContacts.find((loc) => loc.id === currentSelectedRFIContact.id)
    ) {
      currentSetSelectedRFIContact(
        availableContacts.length > 0 ? availableContacts[0] : null,
      );
    }
  }, [
    availableContacts,
    currentSelectedRFIContact,
    currentSetSelectedRFIContact,
  ]);

  const onSubmit = async (data) => {
    setLoading(true);

    // When vendor has no displayable contacts (same as when additional section is shown), require first additional contact fields and block submit if invalid
    if (!hasDisplayableApiContacts && additionalContacts?.length > 0) {
      const first = additionalContacts[0];
      const prefix = "additional_0_";
      clearErrors([
        `${prefix}FirstName`,
        `${prefix}LastName`,
        `${prefix}Title`,
        `${prefix}Email`,
      ]);
      let hasError = false;
      if (!String(first?.firstName ?? "").trim()) {
        setError(`${prefix}FirstName`, { type: "manual", message: "First name is required" });
        hasError = true;
      }
      if (!String(first?.lastName ?? "").trim()) {
        setError(`${prefix}LastName`, { type: "manual", message: "Last name is required" });
        hasError = true;
      }
      if (!String(first?.title ?? "").trim()) {
        setError(`${prefix}Title`, { type: "manual", message: "Job title is required" });
        hasError = true;
      }
      if (!String(first?.email ?? "").trim()) {
        setError(`${prefix}Email`, { type: "manual", message: "Email is required" });
        hasError = true;
      }
      if (hasError) {
        setLoading(false);
        return;
      }
    }

    const providerDataRoles = {
      provider_user_subrole: [
        {
          Subrole: "Vendor Admin",
        },
      ],
      provider_user_permissions: newVendorRightsList.map((permission) => ({
        permission: permission,
      })),
    };

    let uniqueName = "";
    const currentRFILocation = currentSelectedRFILocation?.id === "new-location"
    ? {
      address: primaryAddress,
      city: primaryCity,
      state: primaryState,
      country: primaryCountry
    } : currentSelectedRFILocation;
    
    for (let services of data?.serviceCategories) {
      uniqueName = `Diligent - ${services?.label} - ${dateFormatRFi(
        data.expectedDate?.startDate,
      ).toUpperCase()} - ${getLocationFromProvider(currentRFILocation, { short: true, abbvState: true })}`;
      const providerUserId = uuidv4();

      const providerData = {
        email: data?.contactEmail || "",
        provider_user_id: providerUserId, // Use existing ID or generate new one
        provider_id: data.selectedProvider?.provider_id || "",
        phone_number: null,
        office_phone_number: null,
        contact_preference: null,
        first_name: data?.contactFirstName || "",
        last_name: data?.contactLastName || "",
        provider_name: data.selectedProvider?.name || "",
        organization_id: data.selectedProvider?.org_id,
        is_primary: true,
        ...providerDataRoles,
      };

      const rfiContactEmail =
        currentSelectedRFIContact?.id === "new-contact"
          ? additionalContacts[0]?.email
          : currentSelectedRFIContact?.email;

      const rfiRequest = {
        name: uniqueName,
        owner_id: hcUserId,
        template_id: services?.value,
        due_by: data?.expectedDate?.startDate,
        invite_message: data.additionalDetails || "",
        send_questionnaire: true,
        vendor_name: data.selectedProvider?.name || "",
        template_name: services?.label || "",
        emails: [rfiContactEmail?.toLowerCase() || ""],
        provider_id: data.selectedProvider?.provider_id || "",
        is_global_template: services?.global || false,
        location: currentRFILocation?.address || "",
        location_city: currentRFILocation?.city || "",
        location_state: currentRFILocation?.state || "",
        location_country: currentRFILocation?.country || "",
      };

      allContacts?.length === 0 &&
        watch("selectedProvider") &&
        addProviderUser(
          { providerData: providerData },
          {
            onSuccess: () => {
              updateProviderEmail(
                {
                  id: data.selectedProvider?.provider_id,
                  data: data?.contactEmail || "",
                },
                {
                  onSuccess: () => {
                    sendRfis(
                      {
                        id: purchaseVendorBySponsor?.hc_purchase_vendor_id,
                        data: rfiRequest,
                      },
                      {
                        onSuccess: () => {
                          setIsError(false);
                          setNotificationMessage(
                            "VQQ request submitted successfully",
                          );
                          setShowNotification(true);
                          setLoading(false);

                          // Reset form and redirect after a delay
                          setTimeout(() => {
                            reset();
                            navigate("/RFI/History");
                          }, 2000);
                        },
                        onError: (error) => {
                          setIsError(true);
                          setNotificationMessage(
                            error?.response?.data?.message ||
                              "Failed to submit VQQ request",
                          );
                          setShowNotification(true);
                          setLoading(false);
                        },
                      },
                    );
                  },
                },
                {
                  onError: (error) => {
                    setIsError(true);
                    setNotificationMessage(
                      error?.response?.data?.message ||
                        "Failed to update provider organization",
                    );
                    setShowNotification(true);
                    setLoading(false);
                  },
                },
              );
            },
            onError: (error) => {
              setIsError(true);
              setNotificationMessage(
                error?.response?.data?.message || "Failed to add provider user",
              );
              setShowNotification(true);
              setLoading(false);
            },
          },
        );

      allContacts?.length > 0 &&
        watch("selectedProvider") &&
        // TODO: update vendor contacts and then send RFIs
        sendRfis(
          {
            id: purchaseVendorBySponsor?.hc_purchase_vendor_id,
            data: rfiRequest,
          },
          {
            onSuccess: () => {
              setIsError(false);
              setNotificationMessage("VQQ request submitted successfully");
              setShowNotification(true);
              setLoading(false);

              // Reset form and redirect after a delay
              setTimeout(() => {
                reset();
                navigate("/RFI/History");
              }, 2000);
            },
            onError: (error) => {
              setIsError(true);
              setNotificationMessage(
                error?.response?.data?.message ||
                  "Failed to submit VQQ request",
              );
              setShowNotification(true);
              setLoading(false);
            },
          },
        );

      // Call the mutation
    }

    const providerId = data.selectedProvider?.provider_id || "";
    const organization_id = data.selectedProvider?.org_id || "";

    // When allContacts.length === 0 we only add the user via addProviderUser above; do not run addProviderContact/addProviderLocation here
    // so that if addProviderUser fails, no other API calls are made.
    const isNoContactsPath = allContacts?.length === 0;

    const vendorContacts =
      !isNoContactsPath &&
      currentSelectedRFIContact?.id === "new-contact"
        ? [
            ...additionalContacts?.map((contact) => ({
              provider_id: providerId,
              organization_id,
              email: contact.email,
              first_name: contact.firstName,
              last_name: contact.lastName,
              job_title: contact.title,
              is_primary: false,
              provider_name: data.selectedProvider?.name || "",
              ...providerDataRoles,
            })),
          ]
        : [];
    // send contacts to endpoint (use a loop to call multiple times the endpoint)
    if (vendorContacts && vendorContacts.length > 0) {
      try {
        await Promise.all(
          vendorContacts.map((contact) => addProviderContact(contact)),
        );
      } catch (error) {
        setLoading(false);
        setIsError(true);
        setNotificationMessage(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to add contact. Please check the contact details and try again.",
        );
        setShowNotification(true);
        return;
      }
    }

    const vendorLocations =
      !isNoContactsPath &&
      currentSelectedRFILocation?.id === "new-location"
        ? [
            {
              address1: primaryAddress || "",
              city: primaryCity || "",
              country: primaryCountry || "",
              state: primaryState || "",
              provider_id: providerId,
              location_type: "additional",
            },
          ]
        : [];
    // send locations to endpoint (use the new endpoint to send the list)
    if (vendorLocations && vendorLocations.length > 0) {
      try {
        await Promise.all(
          vendorLocations.map((location) => {
            return addProviderLocation({ provider_id: providerId, location });
          }),
        );
        refetchLocations();
      } catch (error) {
        setLoading(false);
        setIsError(true);
        setNotificationMessage(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to add location. Please check the address and try again.",
        );
        setShowNotification(true);
        return;
      }
    }
  };

  if (isLoading) {
    return <LoadingModal open={isLoading} />;
  }

  // Check if form can be submitted
  const canSubmit = () => {
    const hasContacts = allContacts?.length > 0;
    if (hasContacts) return true;

    // If no contacts, check if all three fields are filled
    const firstName = watch("contactFirstName");
    const lastName = watch("contactLastName");
    const email = watch("contactEmail");

    return (
      firstName &&
      lastName &&
      email &&
      firstName.trim() !== "" &&
      lastName.trim() !== "" &&
      email.trim() !== ""
    );
  };

  const addAdditionalContact = () => {
    setAdditionalContacts((prev) => [
      ...prev,
      {
        firstName: "",
        lastName: "",
        title: "",
        email: "",
      },
    ]);
  };

  const updateAdditionalContact = (index, field, value) => {
    setAdditionalContacts((prev) =>
      prev.map((contact, i) =>
        i === index ? { ...contact, [field]: value } : contact,
      ),
    );
  };

  const removeAdditionalContact = (index) => {
    setAdditionalContacts((prev) => prev.filter((_, i) => i !== index));
  };

  const handleOnLoad = (autocompleteInstance) => {
    setAutocomplete(autocompleteInstance);
  };

  const handlePlaceChanged = () => {
    if (autocomplete) {
      const place = autocomplete.getPlace();

      const { streetAddress, city, state, countryCode } =
        parseAddressComponents(place);

      setPrimaryAddress(streetAddress);
      setPrimaryCity(city);
      setPrimaryState(state);
      setPrimaryCountry(countryCode);

      setValue("primaryAddress", streetAddress);
      setValue("primaryCity", city);
      setValue("primaryState", state);
      setValue("primaryCountry", countryCode);
      setIsPrimaryCountryDropdownOpen(false);
      setIsPrimaryStateDropdownOpen(false);
    }
  };

  return (
    <div className="container">
      <PageHeader
        heading={"Vendor Qualification Questionnaire (VQQ)"}
        subHeading={
          "Complete the information below to request a Vendor Qualification Questionnaire (VQQ) document/(s) to be completed by a selected vendor."
        }
        marginLeft={"ml-5"}
      />

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="ml-5 sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-3 lg:py-3">
            <h1 className="block text-sm font-medium text-gray-900 mt-4 md:mt-0">
              Vendor Name
            </h1>
            <div className="flex flex-col space-y-3">
              <span className="space-x-3">
                <ComboBox
                  data={providers}
                  placeHolder="Select Vendors"
                  setSelected={(value) => setValue("selectedProvider", value)}
                  selected={watch("selectedProvider")}
                  width={"w-[500px]"}
                  inputWidth={"w-[500px]"}
                  showContactFormatting={true}
                />
              </span>
              {errors.selectedProvider && (
                <p className="text-red-500 text-sm">
                  {errors.selectedProvider.message}
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-col border-b border-gray-300 py-5 lg:py-6">
            <div className="ml-5  sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 py-5 lg:py-6">
              <div className="flex items-center justify-between">
                <h1 className="block text-sm font-medium text-gray-900 md:mt-0">
                  Contact
                </h1>
              </div>
              <div className="mt-2 sm:col-span-2 sm:mt-0 w-[330px] lg:w-[400px] xl:w-[500px]">
                {availableContacts.length > 0 &&
                availableContacts[0].id !== "" ? (
                  <>
                    {
                      // Multiple contacts - show custom dropdown with bold labels
                      <div className="sm:max-w-xl relative rfi-location-dropdown">
                        <div
                          className={`flex rounded-md shadow-sm ring-1 ring-inset ${errors.rfiContact ? "ring-red-300" : "ring-gray-200"} focus-within:ring-2 focus-within:ring-inset ${errors.rfiContact ? "focus-within:ring-red-500" : "focus-within:ring-indigo-600"} hover:bg-gray-50 transition-colors`}
                        >
                          <button
                            type="button"
                            onClick={() =>
                              setIsRFIContactDropdownOpen(
                                !isRFIContactDropdownOpen,
                              )
                            }
                            onBlur={(e) =>
                              onBlurSelectWithDelay(
                                e,
                                setIsRFIContactDropdownOpen,
                              )
                            } // Use the new onBlurSelect function
                            className="relative w-full cursor-pointer bg-transparent py-1.5 pl-3 pr-10 text-left border-0 focus:ring-0 focus:outline-none sm:text-sm sm:leading-6"
                          >
                            <span className="block truncate text-gray-900">
                              {currentSelectedRFIContact ? (
                                (() => {
                                  const selected = availableContacts.find(
                                    (loc) =>
                                      loc.id === currentSelectedRFIContact?.id,
                                  );
                                  return selected ? (
                                    selected?.id === "new-contact" ? (
                                      <span className="italic text-gray-500">
                                        Add a contact that is not listed above
                                      </span>
                                    ) : (
                                      // Regular contact display
                                      <>
                                        <span className="font-medium">
                                          {selected.label}:
                                        </span>{" "}
                                        {selected.fullContact}
                                      </>
                                    )
                                  ) : (
                                    "Select VQQ contact"
                                  );
                                })()
                              ) : (
                                <span className="text-gray-500">
                                  Select VQQ contact
                                </span>
                              )}
                            </span>
                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                              <ChevronDownIconSolid
                                className="h-5 w-5 text-gray-400"
                                aria-hidden="true"
                              />
                            </span>
                          </button>
                        </div>

                        {isRFIContactDropdownOpen && (
                          <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                            {availableContacts.map((contact) => (
                              <div
                                key={contact.id}
                                className={`cursor-pointer select-none relative py-2 pl-3 pr-9 ${
                                  contact.isNewLocation
                                    ? "border-t border-gray-200 mt-1 pt-3" // Add separator before "New Location"
                                    : ""
                                } ${
                                  currentSelectedRFIContact?.id === contact.id
                                    ? "text-indigo-600 bg-indigo-50"
                                    : "text-gray-900 hover:bg-gray-50"
                                }`}
                                onClick={() => {
                                  currentSetSelectedRFIContact(contact);
                                  if (contact.id === "new-contact") {
                                    setAdditionalContacts([contact]);
                                  }
                                  setIsRFIContactDropdownOpen(false);
                                }}
                              >
                                <span className="block truncate">
                                  {contact?.id === "new-contact" ? (
                                    // Special styling for "New Location"
                                    <span className="italic text-gray-500">
                                      Add a contact that is not listed above
                                    </span>
                                  ) : (
                                    // Regular location display
                                    <>
                                      <span className="font-medium">
                                        {contact.label}:
                                      </span>{" "}
                                      {contact.fullContact}
                                    </>
                                  )}
                                </span>
                                {currentSelectedRFIContact?.id ===
                                  contact.id && (
                                  <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                                    <CheckIcon
                                      className="h-5 w-5 text-indigo-600"
                                      aria-hidden="true"
                                    />
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    }
                  </>
                ) : (
                  // No contacts available - show message like preferred contact
                  <div className="sm:max-w-xl text-sm sm:pt-1.5">
                    <span className="text-gray-500 italic">
                      Please first select or add a vendor in the section above.
                    </span>
                  </div>
                )}
                {errors.rfiContact && (
                  <p className="mt-2 text-sm text-red-600">
                    {errors.rfiContact}
                  </p>
                )}
              </div>
            </div>

            {(availableContacts?.length === 0 ||
              currentSelectedRFIContact?.id === "new-contact") &&
              additionalContacts.map((contact, index) => {
                const contactLabel =
                  index === 0
                    ? "Additional Contact"
                    : `Additional Contact ${index + 1}`;
                const isRequiredContact =
                  apiContacts?.length === 0 && index === 0;
                return (
                  <div
                    key={index}
                    className={`ml-5 sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 ${index === 0 ? "sm:border-t sm:border-gray-200" : ""}`}
                  >
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                        {contactLabel}
                        {isRequiredContact && (
                          <span className="text-red-600 ml-0.5" aria-hidden>
                            *
                          </span>
                        )}
                      </label>
                      {availableContacts?.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            removeAdditionalContact(index);
                            currentSetSelectedRFIContact(null);
                          }}
                          className="text-gray-400 hover:text-red-500 transition-colors"
                          aria-label="Remove contact"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <div className="mt-2 sm:col-span-2 sm:mt-0 w-[330px] lg:w-[400px] xl:w-[500px]">
                      <ContactFields
                        errors={errors}
                        setValue={setValue}
                        fieldPrefix={`additional_${index}_`}
                        firstName={contact.firstName}
                        setFirstName={(v) =>
                          updateAdditionalContact(index, "firstName", v)
                        }
                        lastName={contact.lastName}
                        setLastName={(v) =>
                          updateAdditionalContact(index, "lastName", v)
                        }
                        title={contact.title}
                        setTitle={(v) =>
                          updateAdditionalContact(index, "title", v)
                        }
                        email={contact.email}
                        setEmail={(v) =>
                          updateAdditionalContact(index, "email", v)
                        }
                        fullWidth
                        errorHighlight={false}
                      />
                    </div>
                  </div>
                );
              })}

            {/* Add Additional Contact Button - Left margin */}
            {/* <div className="ml-5 sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:pt-6 sm:pb-2">
              <div className="sm:col-span-1">
                <button
                  type="button"
                  onClick={addAdditionalContact}
                  className="text-indigo-600 text-sm cursor-pointer hover:underline hover:text-indigo-700 focus:outline-none"
                >
                  Add Additional Contact
                </button>
              </div>
              <div className="sm:col-span-2"></div>
            </div> */}
          </div>
          {/* ProviderContactDrawer for adding contacts */}
          <ProviderContactDrawer
            open={contactDrawerOpen}
            setOpen={setContactDrawerOpen}
            register={register}
            watch={watch}
            onAddContact={handleAddContact}
          />
          <div className="ml-5 sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-3 lg:py-3">
            <h1 className="block text-sm font-medium text-gray-900 mt-4 md:mt-0">
              Questionnaire Templates
            </h1>
            <div className="flex flex-col space-y-3">
              <span className="space-x-3"></span>{" "}
              <div className="w-[330px] lg:w-[400px] xl:w-[500px]">
                <Controller
                  name="serviceCategories"
                  control={control}
                  render={({ field }) => (
                    <TemplateGroupedDropdown
                      placeholder="Select Questionnaire Templates"
                      options={optionsForMultiSelect || []}
                      rfiList={rfis || []}
                      lockedValue="106c4bdc-e1c2-4fca-9ab6-745b995c65ee"
                      selected={field.value || []}
                      onChange={(next) => {
                        setValue("serviceCategories", next);
                      }}
                    />
                  )}
                />
              </div>
              {errors.serviceCategories && (
                <p className="text-red-500 text-sm">
                  {errors.serviceCategories.message}
                </p>
              )}
            </div>
          </div>
          <div className="ml-5 sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-3 lg:py-3">
            <h1 className="block text-sm font-medium text-gray-900 mt-4 md:mt-0">
              Location
            </h1>

            <div className="mt-2 sm:col-span-2 sm:mt-0 w-[330px] lg:w-[400px] xl:w-[500px]">
              {availableLocations.length > 0 &&
              availableLocations[0].id !== "new-location" ? (
                <>
                  <div className="sm:max-w-xl relative rfi-location-dropdown">
                    <div
                      className={`flex rounded-md shadow-sm ring-1 ring-inset ${errors.rfiLocation ? "ring-red-300" : "ring-gray-200"} focus-within:ring-2 focus-within:ring-inset ${errors.rfiLocation ? "focus-within:ring-red-500" : "focus-within:ring-indigo-600"} hover:bg-gray-50 transition-colors`}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setIsRFILocationDropdownOpen(
                            !isRFILocationDropdownOpen,
                          )
                        }
                        onBlur={(e) =>
                          onBlurSelectWithDelay(e, setIsRFILocationDropdownOpen)
                        } // Use the new onBlurSelect function
                        className="relative w-full cursor-pointer bg-transparent py-1.5 pl-3 pr-10 text-left border-0 focus:ring-0 focus:outline-none sm:text-sm sm:leading-6"
                      >
                        <span className="flex items-center truncate text-gray-900">
                          {currentSelectedRFILocation ? (
                            (() => {
                              const selected = availableLocations.find(
                                (loc) =>
                                  loc.id === currentSelectedRFILocation?.id,
                              );
                              return selected ? (
                                selected.id === "new-location" ? (
                                  // Special styling for "New Location" option
                                  <span className="italic text-gray-500">
                                    Request a location that is not listed above
                                  </span>
                                ) : (
                                  // Regular location display
                                    <>
                                      <span className="font-medium">
                                        {selected.label}:
                                      </span>{" "}
                                      <CustomTooltip forceTooltip={true} cutOffAfter={40} sliceLength={40} toolTipContent={selected.fullAddress}>
                                        {selected.fullAddress}
                                      </CustomTooltip>
                                    </>
                                )
                              ) : (
                                "Select VQQ location"
                              );
                            })()
                          ) : (
                            <span className="text-gray-500">
                              Select VQQ location
                            </span>
                          )}
                        </span>
                        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                          <ChevronDownIconSolid
                            className="h-5 w-5 text-gray-400"
                            aria-hidden="true"
                          />
                        </span>
                      </button>
                    </div>

                    {isRFILocationDropdownOpen && (
                      <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                        {availableLocations.map((location) => (
                          <div
                            key={location.id}
                            className={`cursor-pointer select-none relative py-2 pl-3 pr-9 ${
                              location.isNewLocation
                                ? "border-t border-gray-200 mt-1 pt-3" // Add separator before "New Location"
                                : ""
                            } ${
                              currentSelectedRFILocation?.id === location.id
                                ? "text-indigo-600 bg-indigo-50"
                                : "text-gray-900 hover:bg-gray-50"
                            }`}
                            onClick={() => {
                              currentSetSelectedRFILocation(location);
                              setIsRFILocationDropdownOpen(false);
                            }}
                          >
                            <span className="flex items-center truncate">
                              {location.id === "new-location" ? (
                                // Special styling for "New Location"
                                <span className="italic text-gray-500">
                                  Request a location that is not listed above
                                </span>
                              ) : (
                                // Regular location display
                                <>
                                  <span className="font-medium">
                                    {location.label}:
                                  </span>{" "}
                                  <CustomTooltip forceTooltip={true} cutOffAfter={40} sliceLength={40} toolTipContent={location.fullAddress}>
                                    {location.fullAddress}
                                  </CustomTooltip>
                                </>
                              )}
                            </span>
                            {currentSelectedRFILocation?.id === location.id && (
                              <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                                <CheckIcon
                                  className="h-5 w-5 text-indigo-600"
                                  aria-hidden="true"
                                />
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                // No locations available - show message like preferred contact
                <div className="sm:max-w-xl text-sm sm:pt-1.5">
                  <span className="text-gray-500 italic">
                    Please first select or add a vendor in the section above.
                  </span>
                </div>
              )}
              {errors.rfiLocation && (
                <p className="mt-2 text-sm text-red-600">
                  {errors.rfiLocation}
                </p>
              )}
            </div>
          </div>
          {currentSelectedRFILocation?.id === "new-location" && (
            <div className="ml-5 sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-3 lg:py-3">
              <div className="flex items-center justify-between sm:pt-1.5">
                <h1 className="block text-sm font-medium text-gray-900 mt-4 md:mt-0">
                  Add new Location
                </h1>
                {availableLocations?.length > 1 && (
                  <button
                    type="button"
                    onClick={() => {
                      currentSetSelectedRFILocation(
                        availableLocations?.find(loc => loc.location_type === "primary") || availableLocations[0] || null,
                      );
                      setPrimaryAddress("");
                      setPrimaryCity("");
                      setPrimaryState("");
                      setPrimaryCountry("");
                    }}
                    className="text-gray-400 hover:text-red-500 transition-colors"
                    aria-label="Remove location"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
              <LocationFields
                control={control}
                errors={errors}
                handleOnLoad={handleOnLoad}
                handlePlaceChanged={handlePlaceChanged}
                address={primaryAddress}
                setAddress={setPrimaryAddress}
                setValue={setValue}
                onChangeLocation={() => {}}
                city={primaryCity}
                setCity={setPrimaryCity}
                state={primaryState}
                setState={setPrimaryState}
                country={primaryCountry}
                setCountry={setPrimaryCountry}
                countrySearch={primaryCountrySearch}
                setCountrySearch={setPrimaryCountrySearch}
                isCountryDropdownOpen={isPrimaryCountryDropdownOpen}
                setIsCountryDropdownOpen={setIsPrimaryCountryDropdownOpen}
                isStateDropdownOpen={isPrimaryStateDropdownOpen}
                setIsStateDropdownOpen={setIsPrimaryStateDropdownOpen}
                stateSearch={primaryStateSearch}
                setStateSearch={setPrimaryStateSearch}
                classes="w-[330px] lg:w-[400px] xl:w-[500px]"
              />
            </div>
          )}

          <div className="ml-5 sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6 py-5 lg:py-6">
            <h1 className="block text-sm font-medium text-gray-900 mt-4 md:mt-0">
              Expected Due Date
            </h1>{" "}
            <div className="flex flex-col space-y-3">
              <span className="space-x-3">
                <div className="w-[330px] lg:w-[400px] xl:w-[500px]">
                  <DatePickerController
                    control={control}
                    fieldName={"expectedDate"}
                    className="w-full"
                    minDate={new Date()}
                  />
                </div>
              </span>
              {errors.expectedDate && (
                <p className="text-red-500 text-sm">
                  {errors.expectedDate.message ||
                    "Expected due date is required"}
                </p>
              )}
            </div>
          </div>
          <div className="ml-5 sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-3 lg:py-3">
            <h1 className="block text-sm font-medium text-gray-900">
              Additional Details
            </h1>
            <div>
              <textarea
                {...register("additionalDetails")}
                placeholder="Provide additional request details"
                className="border-gray-300 rounded-md text-sm  w-[330px] lg:w-[400px] xl:w-[500px] "
              />
            </div>
          </div>
          <div className="mt-10 flex items-center justify-end gap-x-6 py-2 px-5">
            <button
              type="button"
              onClick={() => navigate("/RFI/History")}
              className="inline-flex justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`inline-flex justify-center rounded-md ${
                isLoading || !canSubmit() || isDuringMaintenance() ? "bg-gray-300" : "bg-indigo-600"
              } px-3 py-2 text-sm font-semibold text-white shadow-sm ${
                isLoading || !canSubmit() || isDuringMaintenance()
                  ? "hover:bg-gray-300"
                  : "hover:bg-indigo-500"
              } focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600`}
              disabled={isLoading || !canSubmit() || isDuringMaintenance()}
            >
              {isLoading ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </FormProvider>

      <NotificationModal
        setOpen={setShowNotification}
        open={showNotification}
        title={isError ? "Error" : "VQQ Request Submitted"}
        desc={notificationMessage}
        error={isError}
      />
    </div>
  );
};
